/**
 * Shared utilities for flash message icons and styling.
 * Used by both FlashMessage component and FlashMessages hook.
 */

export enum FlashMessageType {
  Warning = "warning",
  Error = "error",
  Success = "success",
  Info = "info",
}

/**
 * Path data for Heroicons outline exclamation-circle icon.
 */
const ALERT_CIRCLE_PATH =
  "M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z";

/**
 * Path data for Heroicons outline check-circle icon.
 */
const CHECK_CIRCLE_PATH =
  "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z";

/**
 * Creates an SVG element with the given path data for string rendering.
 * Used in Alpine.js templates.
 */
export const createSVGString = (classes: string, pathData: string): string => {
  const strokeClasses = 'stroke="currentColor" stroke-width="1.5" fill="none"';
  return `<svg class="${classes}" viewBox="0 0 24 24" ${strokeClasses}><path stroke-linecap="round" stroke-linejoin="round" d="${pathData}" /></svg>`;
};

/**
 * Creates an SVG element for DOM manipulation.
 * Used in Phoenix LiveView hooks.
 */
export const createSVGElement = (pathData: string): SVGSVGElement => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "h-6 w-6");
  svg.setAttribute("fill", "none");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.5");

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  path.setAttribute("d", pathData);

  svg.appendChild(path);
  return svg;
};

export const getAlertCircleIconString = (classes: string): string => {
  return createSVGString(classes, ALERT_CIRCLE_PATH);
};

export const getCheckCircleIconString = (classes: string): string => {
  return createSVGString(classes, CHECK_CIRCLE_PATH);
};

export const createAlertCircleIcon = (): SVGSVGElement => {
  return createSVGElement(ALERT_CIRCLE_PATH);
};

export const createCheckCircleIcon = (): SVGSVGElement => {
  return createSVGElement(CHECK_CIRCLE_PATH);
};

/**
 * Returns the appropriate icon SVG string based on the flash message type.
 */
export const getIconSVGString = (type: string): string => {
  const baseClasses = "h-6 w-6";

  switch (type) {
    case FlashMessageType.Warning:
    case FlashMessageType.Error:
      return getAlertCircleIconString(baseClasses);
    case FlashMessageType.Success:
    case FlashMessageType.Info:
      return getCheckCircleIconString(baseClasses);
    default:
      return getCheckCircleIconString(baseClasses);
  }
};

/**
 * Returns the appropriate icon SVG element based on the flash message type.
 */
export const createIconSVG = (type: string): SVGSVGElement => {
  switch (type) {
    case FlashMessageType.Warning:
    case FlashMessageType.Error:
      return createAlertCircleIcon();
    case FlashMessageType.Success:
    case FlashMessageType.Info:
      return createCheckCircleIcon();
    default:
      return createCheckCircleIcon();
  }
};

/**
 * Returns the CSS class for the icon container based on the flash message type.
 */
export const getIconClass = (type: string): string => {
  switch (type) {
    case FlashMessageType.Warning:
      return "text-warning";
    case FlashMessageType.Error:
      return "";
    case FlashMessageType.Success:
      return "text-success";
    case FlashMessageType.Info:
      return "text-info";
    default:
      return "";
  }
};

/**
 * Returns the default title based on the flash message type.
 */
export const getTitle = (type: string): string => {
  switch (type) {
    case FlashMessageType.Warning:
      return "This is just a warning!";
    case FlashMessageType.Error:
      return "Something went wrong!";
    case FlashMessageType.Success:
      return "Tasks successful!";
    case FlashMessageType.Info:
      return "Important information!";
    default:
      return "";
  }
};
